import * as React from 'react';
import { Slider } from '../Form/Slider';
import TrashcanIcon from '../Icons/TrashcanIcon';
import { classNames } from '../../../utils';
import { FComponent } from '../../../types/common';
import { ChangeEvent } from '../../../hooks/useForm';
import { useLogger } from '../../../hooks/useLogger';
import { PacketQuestion } from '../../../types/packet.schema';
import { FileInput } from '../Form/FileInput';
import {
  isCheckboxGroupField,
  isDateField,
  isFileInputField,
  PacketFormFieldValue
} from '../../../hooks/useSellersPacketForm';
import {
  CheckboxGroup,
  DateInput,
  Input,
  RadioGroup,
  RadioGroupOption,
  Select
} from '../Form';
import { LinkField } from '../Form/LinkField';
import { memoize } from '../../../utils/memoize';
import { ImageRadioGroupOption } from '../Form/ImageRadioGroupOption';
import { useUserAgent } from '../../../hooks/useUserAgent';
import { Combobox } from '../Form/Combobox';
import { useCombobox } from '@hooks/useCombobox';

type PacketFormFieldProps = {
  question: PacketQuestion;
  name: string;
  value: PacketFormFieldValue;
  handleChange: (event: ChangeEvent | React.ChangeEvent<HTMLInputElement>) => void;
  acceptedFileExtensions: string[];
  maxSize: number;
  minSize: number;
  onDelete?: (id: string) => void;
  isFieldVisited: (fieldName: string) => boolean;
  errors: Record<string, string[]>;
  disabled?: boolean;
};

// memoized because it is (potentially) recomputed on every render
const getOptionGroup = memoize((choices: PacketQuestion['boundedChoices']) =>
  choices.map(choice => ({
    value: choice.text,
    label: choice.label || choice.text
  }))
);

const getBoundedChoicesText = memoize((choices: PacketQuestion['boundedChoices']) =>
  choices.map(choice => choice.text)
);

// This component dynamically displays a a form field component based on the question type.

const PacketFormField: FComponent<PacketFormFieldProps> = ({
  question,
  name,
  value,
  handleChange,
  acceptedFileExtensions,
  maxSize,
  minSize,
  isFieldVisited,
  onDelete,
  errors,
  disabled = false
}) => {
  const log = useLogger('PacketForm');
  const { isMobile } = useUserAgent();

  const boundedChoicesText = getBoundedChoicesText(question.boundedChoices);
  const comboboxProps = useCombobox<string>({
    initialOptions: boundedChoicesText,
    setOptionsOnQuery: (query, setOptions) =>
      setOptions(
        boundedChoicesText.filter(choice =>
          choice.toLowerCase().includes(query.toLowerCase())
        )
      ),
    getOptionLabel: option => option,
    compareBy: (a, b) => a === b
  });

  // We currently have no dynamic fields with boolean values.
  if (typeof value === 'boolean') {
    return null;
  }

  if (isCheckboxGroupField(question, value)) {
    return (
      <CheckboxGroup
        name={name}
        options={question.boundedChoices.map(choice => ({
          value: choice.id,
          label: choice.imageUrl ? choice.label : choice.label || choice.text,
          ariaLabel: choice.ariaLabel,
          imageUrl: choice.imageUrl
        }))}
        onChange={handleChange}
        checked={value}
        visited={isFieldVisited(name)}
        errors={errors[name]}
        disabled={disabled}
      />
    );
  }

  // this typeguard saves us having to cast all over the place below.
  if (isFileInputField(question, value)) {
    return (
      <FileInput
        name={name}
        onChange={handleChange}
        acceptedFileExtensions={acceptedFileExtensions}
        value={value}
        multiple={question.multiple}
        maxSize={maxSize}
        minSize={minSize}
        visited={isFieldVisited(name)}
        errors={errors[name]}
        disabled={disabled}
      />
    );
  }

  if (isDateField(question, value)) {
    return (
      <WithDelete
        className="with-bottom-margin"
        onDelete={onDelete}
        id={name}
        disabled={disabled}>
        <DateInput
          name={name}
          value={value === '' ? undefined : new Date(value)}
          ariaLabel={question.text}
          onChange={handleChange}
          visited={isFieldVisited(name)}
          errors={errors[name]}
          disabled={disabled}
        />
      </WithDelete>
    );
  }

  switch (question.htmlInputType) {
    case 'color':
    case 'datetime-local':
    case 'month':
    case 'password':
    case 'time':
    case 'week': {
      log.exception({
        errorType: 'NotImplemented',
        error: `Not Implemented: ${question.htmlInputType}`,
        tags: { questionId: question.id }
      });
      return null;
    }
    case 'radio': {
      const isImagePicker = question.boundedChoices.some(option =>
        Boolean(option.imageUrl)
      );
      return (
        <RadioGroup
          name={name}
          value={value}
          ariaLabel={question.text}
          instruction={
            isImagePicker && !isMobile ? 'Press and hold the image to zoom in' : undefined
          }
          containerClassName={isImagePicker ? 'image-radiogroup' : undefined}
          className={classNames(
            isImagePicker ? 'option-group-grid' : 'question-radiogroup'
          )}
          onChange={handleChange}
          visited={isFieldVisited(name)}
          errors={errors[name]}
          disabled={disabled}>
          {question.boundedChoices.map(({ id, text, label, imageUrl, ariaLabel }) =>
            imageUrl ? (
              <ImageRadioGroupOption
                key={id}
                value={text}
                label={label}
                ariaLabel={ariaLabel}
                imageUrl={imageUrl}
              />
            ) : (
              <RadioGroupOption key={id} value={text} label={label || text} />
            )
          )}
        </RadioGroup>
      );
    }
    case 'text':
    case 'tel':
    case 'email':
    case 'number':
    case 'url': {
      return (
        <WithDelete
          className="with-bottom-margin"
          onDelete={onDelete}
          id={name}
          disabled={disabled}>
          <Input
            name={name}
            value={value}
            type={question.htmlInputType}
            ariaLabel={question.text}
            onChange={handleChange}
            visited={isFieldVisited(name)}
            errors={errors[name]}
            disabled={disabled}
          />
        </WithDelete>
      );
    }
    case 'combobox': {
      return (
        <Combobox
          name={name}
          value={value}
          onChange={handleChange}
          ariaLabel={question.text}
          visited={isFieldVisited(name)}
          errors={errors[name]}
          disabled={disabled}
          {...comboboxProps}
        />
      );
    }
    case 'select': {
      const optionGroup = getOptionGroup(question.boundedChoices);

      return (
        <Select
          className="no-margin"
          name={name}
          options={optionGroup}
          onChange={handleChange}
          value={value}
          visited={isFieldVisited(name)}
          errors={errors[name]}
          disabled={disabled}
        />
      );
    }
    case 'range': {
      return (
        <Slider
          name={name}
          ariaName={question.text}
          onChange={handleChange}
          min={+question.inputRangeMin}
          max={+question.inputRangeMax}
          step={+question.inputRangeStep}
          defaultValue={+question.inputRangeDefault}
          value={value ? +value : undefined}
          className="default-slider wide-slider-textbox"
          visited={isFieldVisited(name)}
          errors={errors[name]}
          disabled={disabled}
        />
      );
    }
    case 'a': {
      return (
        <LinkField
          name={name}
          href={question.hrefUrl}
          ariaLabel="Link to external site, click to complete the question then come back to this tab"
          value={value}
          onChange={handleChange}
          visited={isFieldVisited(name)}
          errors={errors[name]}
        />
      );
    }
    default:
      log.exception({
        error: `Unknown html input type: ${question.htmlInputType} ${typeof value}`,
        tags: {
          questionId: question.id
        }
      });
      return null;
  }
};

type WithDeleteProps = {
  onDelete?: (id: string) => void;
  id: string;
  disabled?: boolean;
};
const WithDelete: FComponent<WithDeleteProps> = ({
  id,
  className,
  onDelete,
  children,
  disabled = false
}) => {
  id;
  return (
    <div className={classNames('field-with-delete', className)}>
      {children}
      {onDelete && !disabled ? (
        <button
          className="delete-button"
          type="button"
          onClick={() => onDelete(id)}
          aria-label={`delete answer`}>
          <TrashcanIcon />
        </button>
      ) : null}
    </div>
  );
};

export { PacketFormField };
